import styled from "styled-components"

import {
  Checkbox,
  Dialog,
  Select,
  spacing,
  Text,
  TextField,
} from "@minuthq/meatball-ui-react"

import { ParadiseCountrySelector } from "src/components/Paradise/ParadiseCountrySelector"
import {
  TParadiseCreateUserForm,
  useParadiseCreateUser,
} from "src/components/Paradise/ParadiseUsers/useParadiseCreateUser"
import { RequireRoles } from "src/components/RestrictedRoles/RequireRoles"
import { TCountryCode } from "src/data/ecommerce/ecommerceTypes"
import { usePostParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { TParadiseCreateUserBody } from "src/data/paradise/paradiseUsers/types/paradiseUserQueryTypes"
import { rolesWithParadiseAccess } from "src/data/user/logic/accessLogic"
import { UserRoles } from "src/data/user/user"
import { SUPPORTED_LANGUAGES } from "src/i18n/supportedLanguages.mjs"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { PhoneNumberInput } from "src/ui/PhoneNumberInput/PhoneNumberInput"

const FORM_ID = "paradise-create-user-form"

export function ParadiseCreateUserDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { navigate } = useRouter()

  const postParadiseUser = usePostParadiseUser()

  const {
    form,
    updateBillingAddress,
    availableCountryCodes,
    initializeBillingAddress,
    clearBillingAddress,
    availableStates,
  } = useParadiseCreateUser({
    onSubmit: handleSubmit,
  })

  function handleSubmit(values: TParadiseCreateUserForm) {
    postParadiseUser.mutate(
      {
        fullname: values.fullname,
        email: values.email,
        preferred_language: values.preferredLanguage,
        subscribe_newsletter: values.subscribeNewsletter,
        admin_role: values.adminRole,
        billing_address: !!values.billingAddress
          ? {
              first_name: values.billingAddress.firstName,
              last_name: values.billingAddress.lastName,
              phone: values.billingAddress.phone,
              email: values.billingAddress.email,
              line1: values.billingAddress.line1,
              city: values.billingAddress.city,
              state_code: values.billingAddress.stateCode,
              zip: values.billingAddress.zip,
              country: values.billingAddress.country as TCountryCode,
            }
          : undefined,
      },
      {
        onSuccess: (data) => {
          navigate(Routes.ParadiseUser.location(data.user_id))
        },
      }
    )
  }

  function closeDialog() {
    form.resetForm()
    postParadiseUser.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      title="Create user"
      formId={FORM_ID}
      confirmLabel="Create"
      closeLabel="Cancel"
      loading={postParadiseUser.isLoading}
      error={
        !!postParadiseUser.isError && (
          <div>
            <Text variant="subtitle" color="unset">
              {postParadiseUser.error?.message}
            </Text>
            <Text color="unset">
              {postParadiseUser.error?.response?.data?.error_key}
            </Text>
            <Text color="unset">
              {postParadiseUser.error?.response?.data?.message}
            </Text>
          </div>
        )
      }
    >
      <Form id={FORM_ID} onSubmit={form.handleSubmit}>
        <TextField
          label="Fullname"
          name="fullname"
          value={form.values.fullname}
          onChange={(value) => form.setFieldValue("fullname", value)}
          required
          requiredIndicator
        />

        <TextField
          label="Email"
          name="email"
          type="email"
          value={form.values.email}
          onChange={(value) => form.setFieldValue("email", value)}
          required
          requiredIndicator
        />

        <Select
          label="Preferred language"
          name="preferred_language"
          value={form.values.preferredLanguage ?? ""}
          onChange={(value) => form.setFieldValue("preferredLanguage", value)}
          options={SUPPORTED_LANGUAGES.map((language) => ({
            label: language,
            value: language,
          }))}
          required
          requiredIndicator
        />

        <RequireRoles roles={[UserRoles.SUPERADMIN]}>
          <Select
            label="Admin role"
            name="admin_role"
            value={form.values.adminRole ?? ""}
            options={[
              {
                label: "No role",
                value: "",
              },
              ...rolesWithParadiseAccess.map((role) => ({
                label: role,
                value: role,
              })),
            ]}
            onChange={(value) =>
              form.setFieldValue(
                "adminRole",
                value as TParadiseCreateUserBody["admin_role"]
              )
            }
          />
        </RequireRoles>

        <Checkbox
          label="Add billing address (will create a customer in Chargebee)"
          checked={!!form.values.billingAddress}
          onCheck={(checked) => {
            if (checked) {
              initializeBillingAddress()
            } else {
              clearBillingAddress()
            }
          }}
        />

        {form.values.billingAddress && (
          <Fieldset>
            <Text variant="subtitle">Billing address</Text>
            <Text>
              By adding a billing address, a customer will also be created in
              Chargebee.
            </Text>

            <TextField
              label="First name"
              name="billing-address-first-name"
              value={form.values.billingAddress.firstName}
              onChange={(value) =>
                updateBillingAddress({
                  firstName: value,
                })
              }
              required
              requiredIndicator
            />

            <TextField
              label="Last name"
              name="billing-address-last-name"
              value={form.values.billingAddress.lastName}
              onChange={(value) => updateBillingAddress({ lastName: value })}
              required
              requiredIndicator
            />

            <PhoneNumberInput
              label="Phone"
              value={form.values.billingAddress.phone}
              onChange={(value) => updateBillingAddress({ phone: value })}
              error={
                /** Formik errors are not typed, so we need to cast to the correct type */
                !!((form.errors.billingAddress as unknown as { phone?: string })
                  ?.phone as unknown as string)
              }
              required
            />

            <TextField
              label="Email"
              type="email"
              name="billing-address-email"
              value={form.values.billingAddress.email}
              onChange={(value) => updateBillingAddress({ email: value })}
              required
              requiredIndicator
            />

            <TextField
              label="Line 1"
              name="billing-address-line1"
              value={form.values.billingAddress.line1}
              onChange={(value) => updateBillingAddress({ line1: value })}
              required
              requiredIndicator
            />

            <TextField
              label="City"
              name="billing-address-city"
              value={form.values.billingAddress.city}
              onChange={(value) => updateBillingAddress({ city: value })}
              required
              requiredIndicator
            />

            <TextField
              label="Zip"
              name="billing-address-zip"
              required
              requiredIndicator
              value={form.values.billingAddress.zip}
              onChange={(value) => updateBillingAddress({ zip: value })}
            />

            <ParadiseCountrySelector
              label="Country"
              name="billing-address-country"
              countryCodes={availableCountryCodes ?? []}
              required
              requiredIndicator
              selectedCountryCode={form.values.billingAddress.country}
              onSelectCountryCode={(value) =>
                updateBillingAddress({ country: value })
              }
            />

            <Select
              label="State code"
              name="billing-address-state-code"
              value={form.values.billingAddress.stateCode ?? ""}
              onChange={(value) => updateBillingAddress({ stateCode: value })}
              options={[
                {
                  label: "",
                  value: "",
                  hidden: true,
                },
                ...(availableStates ?? []).map((state) => ({
                  label: state.label,
                  value: state.code,
                })),
              ]}
              disabled={
                !form.values.billingAddress.country ||
                !availableStates ||
                availableStates.length === 0
              }
              required
              requiredIndicator
            />
          </Fieldset>
        )}

        <Checkbox
          label="Subscribe to newsletter"
          checked={form.values.subscribeNewsletter}
          onCheck={(checked) =>
            form.setFieldValue("subscribeNewsletter", checked)
          }
        />
      </Form>
    </Dialog>
  )
}

const Form = styled.form`
  display: grid;
  gap: ${spacing.M};
`

const Fieldset = styled.fieldset`
  all: unset;

  display: grid;
  gap: ${spacing.M};
`
